/**
 * Forecast Mini-Map Widget — EAQI Composite Choropleth
 * Renders a small air quality forecast map in the city widget sidebar.
 * Each county is colored based on the European Air Quality Index (EEA 2024):
 * worst-case sub-index across PM2.5, PM10, O₃, NO₂, SO₂.
 * Clicking navigates to analysis.html.
 */
import { loadCube, timesToDates } from './data.js';
import { computeEAQI, eaqiLevelToColor } from './colormaps.js';

const EAQI_POLLUTANTS = ['pm25', 'pm10', 'o3', 'no2', 'so2'];

let countyGeoJSON = null;

async function loadCountyBoundaries() {
  if (countyGeoJSON) return countyGeoJSON;
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}data/romania_counties_wgs84.geojson`);
    countyGeoJSON = await res.json();
  } catch (e) {
    console.warn('Forecast widget: county boundaries not loaded', e);
    countyGeoJSON = { features: [] };
  }
  return countyGeoJSON;
}

// Point-in-polygon (ray casting)
function pointInPolygon(lon, lat, ring) {
  let inside = false;
  for (let k = 0, n = ring.length, l = n - 1; k < n; l = k++) {
    const [xi, yi] = ring[k], [xj, yj] = ring[l];
    if (((yi > lat) !== (yj > lat)) &&
        (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInFeature(lon, lat, feature) {
  const geom = feature.geometry;
  if (geom.type === 'Polygon') {
    return pointInPolygon(lon, lat, geom.coordinates[0]);
  } else if (geom.type === 'MultiPolygon') {
    for (const poly of geom.coordinates) {
      if (pointInPolygon(lon, lat, poly[0])) return true;
    }
  }
  return false;
}

// Build grid→county mapping
function buildGridMapping(counties, lats, lons) {
  const ny = lats.length;
  const nx = lons.length;
  const mapping = new Map();

  for (const feat of counties.features) {
    mapping.set(feat.properties.name, []);
  }

  for (let j = 0; j < ny; j++) {
    const lat = Number(lats[j]);
    for (let i = 0; i < nx; i++) {
      const lon = Number(lons[i]);
      for (const feat of counties.features) {
        if (pointInFeature(lon, lat, feat)) {
          mapping.get(feat.properties.name).push({ j, i });
          break;
        }
      }
    }
  }
  return mapping;
}

// Centroid of a polygon feature
function featureCentroid(feature) {
  const geom = feature.geometry;
  const rings = geom.type === 'MultiPolygon'
    ? geom.coordinates.map(p => p[0])
    : [geom.coordinates[0]];

  let totalLon = 0, totalLat = 0, totalPts = 0;
  for (const ring of rings) {
    for (const [lon, lat] of ring) {
      totalLon += lon;
      totalLat += lat;
      totalPts++;
    }
  }
  return [totalLon / totalPts, totalLat / totalPts];
}

/**
 * Initialize the forecast mini-map widget.
 */
export async function initForecastWidget() {
  const canvas = document.getElementById('forecast-widget-canvas');
  if (!canvas) return;

  try {
    // Load all EAQI pollutant cubes
    const pollutantCubes = {};
    const loadPromises = EAQI_POLLUTANTS.map(async (p) => {
      try {
        pollutantCubes[p] = await loadCube(p);
      } catch (e) {
        console.warn(`Forecast widget: could not load ${p}`, e);
      }
    });
    await Promise.all(loadPromises);

    const refCube = pollutantCubes.pm25;
    if (!refCube) throw new Error('PM2.5 cube not available');

    const dates = timesToDates(refCube.times, refCube.timeAttrs);

    // Use the first forecast day (same as analysis page "Azi")
    const firstDayStr = dates[0].toISOString().slice(0, 10);
    let startIdx = 0, endIdx = 0;
    for (let i = 0; i < dates.length; i++) {
      if (dates[i].toISOString().slice(0, 10) === firstDayStr) {
        endIdx = i;
      }
    }

    await drawForecastMap(canvas, pollutantCubes, refCube, startIdx, endIdx);
  } catch (e) {
    console.warn('Forecast widget: could not load data', e);
    const widget = document.getElementById('forecast-widget');
    if (widget) widget.style.display = 'none';
  }
}

async function drawForecastMap(canvas, pollutantCubes, refCube, startIdx, endIdx) {
  const [nt, ny, nx] = refCube.shape;

  const latMin = Number(refCube.lats[0]);
  const latMax = Number(refCube.lats[ny - 1]);
  const lonMin = Number(refCube.lons[0]);
  const lonMax = Number(refCube.lons[nx - 1]);

  const midLat = (latMin + latMax) / 2;
  const cosLat = Math.cos(midLat * Math.PI / 180);
  const geoWidth = (lonMax - lonMin) * cosLat;
  const geoHeight = latMax - latMin;
  const aspectRatio = geoWidth / geoHeight;

  const dw = 320;
  const dh = Math.round(dw / aspectRatio);

  canvas.width = dw;
  canvas.height = dh;

  // Compute average grid per pollutant
  const avgGrids = {};
  for (const p of EAQI_POLLUTANTS) {
    const cube = pollutantCubes[p];
    if (!cube) continue;
    const grid = new Float32Array(ny * nx);
    const cnt = new Float32Array(ny * nx);
    for (let t = startIdx; t <= endIdx; t++) {
      for (let j = 0; j < ny * nx; j++) {
        const v = cube.data[t * ny * nx + j];
        if (!Number.isNaN(v)) {
          grid[j] += v;
          cnt[j]++;
        }
      }
    }
    for (let j = 0; j < ny * nx; j++) {
      grid[j] = cnt[j] > 0 ? grid[j] / cnt[j] : NaN;
    }
    avgGrids[p] = grid;
  }

  // Load counties and build mapping
  const counties = await loadCountyBoundaries();
  const gridMapping = buildGridMapping(counties, refCube.lats, refCube.lons);

  // Compute EAQI per county
  const countyEAQI = new Map();
  for (const [name, cells] of gridMapping) {
    const pollutantAvgs = {};
    for (const p of EAQI_POLLUTANTS) {
      if (!avgGrids[p]) continue;
      let sum = 0, cnt = 0;
      for (const { j, i } of cells) {
        const v = avgGrids[p][j * nx + i];
        if (!Number.isNaN(v)) {
          sum += v;
          cnt++;
        }
      }
      pollutantAvgs[p] = cnt > 0 ? sum / cnt : null;
    }
    countyEAQI.set(name, computeEAQI(pollutantAvgs));
  }

  // Draw
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, dw, dh);

  ctx.fillStyle = '#0a0e17';
  ctx.fillRect(0, 0, dw, dh);

  function geoToPixel(lon, lat) {
    const px = ((lon - lonMin) / (lonMax - lonMin)) * dw;
    const py = ((latMax - lat) / (latMax - latMin)) * dh;
    return [px, py];
  }

  // Fill county polygons
  for (const feature of counties.features) {
    const name = feature.properties.name;
    const eaqi = countyEAQI.get(name);
    const [r, g, b] = eaqi ? eaqiLevelToColor(eaqi.level) : [30, 40, 60];
    const fillColor = eaqi ? `rgb(${r}, ${g}, ${b})` : 'rgba(30, 40, 60, 0.5)';

    const geom = feature.geometry;
    const coordSets = geom.type === 'MultiPolygon'
      ? geom.coordinates.map(p => p[0])
      : [geom.coordinates[0]];

    ctx.fillStyle = fillColor;
    ctx.beginPath();
    for (const ring of coordSets) {
      for (let k = 0; k < ring.length; k++) {
        const [lon, lat] = ring[k];
        const [px, py] = geoToPixel(lon, lat);
        if (k === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    }
    ctx.fill();
  }

  // Draw county borders
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 0.8;
  ctx.lineJoin = 'round';

  for (const feature of counties.features) {
    const geom = feature.geometry;
    const coordSets = geom.type === 'MultiPolygon'
      ? geom.coordinates.map(p => p[0])
      : [geom.coordinates[0]];

    for (const ring of coordSets) {
      ctx.beginPath();
      for (let k = 0; k < ring.length; k++) {
        const [lon, lat] = ring[k];
        const [px, py] = geoToPixel(lon, lat);
        if (k === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    }
  }

  // Draw county labels (abbreviated)
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const feature of counties.features) {
    const name = feature.properties.name;
    const mnemonic = feature.properties.mnemonic || name.slice(0, 2).toUpperCase();
    const eaqi = countyEAQI.get(name);
    const [clon, clat] = featureCentroid(feature);
    const [px, py] = geoToPixel(clon, clat);

    if (!eaqi) continue;

    // Just the abbreviation for small widget
    ctx.font = 'bold 7px Inter, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.lineWidth = 2;
    ctx.strokeText(mnemonic, px, py);
    ctx.fillText(mnemonic, px, py);
  }
}
